// New tagged slice for the migrated `_features/markets` feature -
// consolidates TWO duplicate injectEndpoints calls that both existed on
// the same apiSlice for /market routes with overlapping endpoint names
// (the old `features/api/marketApiSlice.js`, since removed entirely, and
// features/MarketsFeature/api/marketsApi.js - a dev "endpoint already
// exists" collision).
//
// Fixes a live bug in the process: the archive/activate toggle
// (changeStatusMarket) built its URL by concatenating apiRoutes.market.
// edit.path ("/market/") with "/changeStatus", producing
// "PATCH /market//changeStatus" - a double slash. Verified against
// simplifyserver/routes/market.js: the real route is
// `router.patch("/changeStatus", ...)` mounted at /market, i.e.
// `PATCH /market/changeStatus` (single slash). Hardcoded correctly here
// instead of string-concatenating from the route table.
//
// Drops getAllMarketsCountriesByOrgId (no hook was ever exported, zero
// consumers) and deleteMarket (confirmed zero consumers; also wrong
// semantics for what the UI needs - it maps to the backend's
// `archiveMarket` handler, which one-way sets status to "archived" with
// no reactivate path, not the bidirectional toggle the Archive/Unarchive
// button actually uses).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const marketApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createMarket: builder.mutation({
      query: (data) => ({
        url: apiRoutes.market.create.path,
        method: apiRoutes.market.create.reqType,
        body: data,
      }),
      invalidatesTags: ["Markets"],
    }),
    getAllMarket: builder.query({
      query: (params) => ({
        url: "/market",
        method: "GET",
        params,
      }),
      providesTags: ["Markets"],
    }),
    // Active MRR per market, already converted to USD and to a monthly amount (server).
    getMarketMrr: builder.query({
      query: () => ({ url: "/market/mrr", method: "GET" }),
      providesTags: ["Markets", "Subscriptions"],
    }),
    getAllMarketsByOrgId: builder.query({
      query: (params) => ({
        url: "/market/getAllMarkets",
        method: "GET",
        params,
      }),
      providesTags: ["Markets"],
    }),
    getMarket: builder.query({
      query: (id) => ({
        url: `/market/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Market", id }],
    }),
    getMarketCountries: builder.query({
      query: (params) => ({
        url: "/market/getMarketCountries",
        method: "GET",
        params,
      }),
      providesTags: ["MarketCountries"],
    }),
    editMarket: builder.mutation({
      query: (data) => ({
        url: apiRoutes.market.edit.path,
        method: apiRoutes.market.edit.reqType,
        body: data,
      }),
      invalidatesTags: (result, error, { _id }) => [
        "Markets",
        { type: "Market", id: _id },
      ],
    }),
    changeStatusMarket: builder.mutation({
      query: (data) => ({
        url: "/market/changeStatus",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Markets",
        { type: "Market", id },
      ],
    }),
    reMapMarket: builder.mutation({
      query: (data) => ({
        url: "/market/remap",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Markets", "MarketCountries"],
    }),
  }),
});

export const {
  useCreateMarketMutation,
  useGetAllMarketQuery,
  useGetAllMarketsByOrgIdQuery,
  useGetMarketMrrQuery,
  useLazyGetAllMarketsByOrgIdQuery,
  useGetMarketQuery,
  useLazyGetMarketQuery,
  useGetMarketCountriesQuery,
  useEditMarketMutation,
  useChangeStatusMarketMutation,
  useReMapMarketMutation,
} = marketApi;
